import { ButtonStyles } from "atoms/constants/button-styles";
import { HeadingPriority } from "atoms/constants/heading-priority";
import Loader from "atoms/loaders/loader";
import Heading from "atoms/typography/heading";
import FeatureFlag from "models/interfaces/feature-flag";
import FeatureFlagRecord from "models/view-models/feature-flag-record";
import ScopedFeatureFlagRecord from "models/view-models/scoped-feature-flag-record";
import CallToActionToggle from "molecules/call-to-action/call-to-action-toggle";
import InputFormField from "molecules/form-fields/input-form-field";
import { ConfirmationModal } from "molecules/modals/confirmation-modal";
import React, { useCallback, useEffect, useState } from "react";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { FeatureFlagScopeType } from "utilities/enumerations/feature-flag-scope-type";
import useListFeatureFlags from "utilities/hooks/feature-flags/use-list-feature-flags";
import useUpdateFeatureFlag from "utilities/hooks/feature-flags/use-update-feature-flag";
import SystemSettingsService from "utilities/services/system-settings/system-settings-service";
import { ToastManager } from "utilities/toast/toast-manager";

const FeatureFlagsPage: React.FunctionComponent = () => {
    const SYSTEM_FEATURE_FLAG_SCOPE = FeatureFlagScopeType.System;
    const {
        featureFlags: unscopedApiValues,
        scopedFeatureFlags: scopedApiValues,
        isLoading,
        listFeatureFlags,
        listScopedFeatureFlags,
    } = useListFeatureFlags();
    const { updateFeatureFlag, updateScopedFeatureFlag } =
        useUpdateFeatureFlag();
    const { globalState, setGlobalState } = useGlobalState();
    const isSuperAdmin =
        globalState.currentIdentity?.user?.isSuperAdmin ?? false;

    const [featureFlags, setFeatureFlags] = useState<FeatureFlagRecord[]>([]);
    const [scopedFeatureFlags, setScopedFeatureFlags] = useState<
        ScopedFeatureFlagRecord[]
    >([]);
    const [flag, setFlag] = useState<FeatureFlagRecord | null>(null);
    const [showUnscopedConfirmationModal, setShowUnscopedConfirmationModal] =
        useState(false);
    const [showScopedConfirmationModal, setShowScopedConfirmationModal] =
        useState(false);

    const updateSystemSettings = useCallback(async () => {
        try {
            const response = await SystemSettingsService.get({});
            setGlobalState(
                globalState.setSystemSettings(response.resultObject)
            );
        } catch (error) {
            ToastManager.error(
                "The feature flag was toggled successfully, but system settings could not be updated. Please refresh or navigate to apply the changes."
            );
        }
    }, [globalState, setGlobalState]);

    const handleUnscopedToggle = async ({ name, isEnabled }: FeatureFlag) => {
        await updateFeatureFlag({ name, isEnabled });

        await updateSystemSettings();
        if (!isLoading) {
            setFeatureFlags((prevFlags) =>
                prevFlags.map((flag) =>
                    flag.name === name ? flag.with({ isEnabled }) : flag
                )
            );
        }
        setFlag(null);
    };

    const handleScopedToggle = async ({ name, isEnabled }: FeatureFlag) => {
        await updateScopedFeatureFlag(
            { name, isEnabled },
            SYSTEM_FEATURE_FLAG_SCOPE
        );

        await updateSystemSettings();
        if (!isLoading) {
            setScopedFeatureFlags((prevFlags) =>
                prevFlags.map((flag) =>
                    flag.name === name ? flag.with({ value: isEnabled }) : flag
                )
            );
        }
        setFlag(null);
    };
    const handleOnUnscopedChange = (flag: FeatureFlagRecord) => {
        setShowUnscopedConfirmationModal(true);
        setFlag(flag);
    };
    const handleOnScopedChange = (scopedFlag: ScopedFeatureFlagRecord) => {
        setShowScopedConfirmationModal(true);
        const flag = new FeatureFlagRecord({
            name: scopedFlag.name,
            isEnabled: scopedFlag.value,
        });
        setFlag(flag);
    };

    const handleCancel = () => {
        setShowUnscopedConfirmationModal(false);
        setShowScopedConfirmationModal(false);
        setFlag(null);
    };

    useEffect(() => {
        listFeatureFlags();
        listScopedFeatureFlags(SYSTEM_FEATURE_FLAG_SCOPE);
    }, [SYSTEM_FEATURE_FLAG_SCOPE, listFeatureFlags, listScopedFeatureFlags]);

    useEffect(() => {
        setFeatureFlags(unscopedApiValues);
        setScopedFeatureFlags(scopedApiValues);
    }, [scopedApiValues, unscopedApiValues]);
    return (
        <div className="c-feature-flag-page">
            <Heading priority={HeadingPriority.Two}>Feature Flags</Heading>
            <div className="c-feature-flag-page__list">
                {isLoading && <Loader />}
                <div className="c-feature-flag-page__list__unscoped">
                    <Heading priority={HeadingPriority.Four}>
                        Unscoped Feature Flags
                    </Heading>
                    {!isLoading &&
                        featureFlags.map((flag) => (
                            <CallToActionToggle
                                key={flag.name}
                                checked={flag.isEnabled}
                                label={flag.name}
                                description=""
                                disabled={!isSuperAdmin}
                                onChange={() => handleOnUnscopedChange(flag)}
                            />
                        ))}
                </div>
                <div className="c-feature-flag-page__list__scoped">
                    <Heading priority={HeadingPriority.Four}>
                        Scoped Feature Flags
                    </Heading>

                    {!isLoading &&
                        scopedFeatureFlags.map((flag) => (
                            <CallToActionToggle
                                key={flag.key}
                                checked={flag.value}
                                label={flag.name}
                                description={flag.description}
                                disabled={!isSuperAdmin}
                                onChange={() => handleOnScopedChange(flag)}
                            />
                        ))}
                    {!isLoading && scopedFeatureFlags.length === 0 && (
                        <p>No scoped feature flags found.</p>
                    )}
                </div>
                {flag !== null && (
                    <FeatureFlagToggleConfirmationModal
                        flag={flag}
                        isVisible={showUnscopedConfirmationModal}
                        onConfirm={() => {
                            handleUnscopedToggle({
                                name: flag.name,
                                isEnabled: !flag.isEnabled,
                            });
                            setShowUnscopedConfirmationModal(false);
                        }}
                        onCancel={handleCancel}
                    />
                )}
                {flag !== null && (
                    <FeatureFlagToggleConfirmationModal
                        flag={flag}
                        isVisible={showScopedConfirmationModal}
                        onConfirm={() => {
                            handleScopedToggle({
                                name: flag.name,
                                isEnabled: !flag.isEnabled,
                            });
                            setShowScopedConfirmationModal(false);
                        }}
                        onCancel={handleCancel}
                    />
                )}
            </div>
        </div>
    );
};

export default FeatureFlagsPage;

const FeatureFlagToggleConfirmationModal: React.FC<{
    flag: FeatureFlagRecord;
    isVisible: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}> = ({ flag, isVisible, onConfirm, onCancel }) => {
    const [flagText, setFlagText] = useState("");
    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    const handleFlagTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (errorMessage !== undefined) setErrorMessage(undefined);
        setFlagText(e.target.value);
    };

    const handleConfirm = () => {
        if (flagText === flag.name) {
            onConfirm();
            setFlagText("");
            setErrorMessage(undefined);
        } else {
            setErrorMessage(
                "The flag name does not match. Please re-type the flag name exactly as shown."
            );
            return;
        }
    };

    const handleCancel = () => {
        setFlagText("");
        setErrorMessage(undefined);
        onCancel();
    };

    return (
        <ConfirmationModal
            label="Confirm Feature Flag Change"
            message={
                <>
                    You are about to change the feature flag:{" "}
                    <strong>{flag.name}</strong>.
                    <br />
                    To confirm, please re-type the flag name exactly as shown.
                </>
            }
            isVisible={isVisible}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
            confirmButtonStyle={ButtonStyles.Primary}>
            <InputFormField
                placeholder={flag.name}
                maxLength={1000}
                autofocus={true}
                label={""}
                onChange={handleFlagTextChange}
                value={flagText}
                errorMessage={errorMessage}
            />
        </ConfirmationModal>
    );
};
